import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface AgentMarkdownRendererProps {
    content: string
    isStreaming?: boolean
}

/** Normalise line endings and fix headings glued to preceding text during streaming. */
const normaliseContent = (text: string): string => {
    const normalised = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const lines = normalised.split('\n')
    let inFence = false

    return lines
        .map(line => {
            const trimmed = line.trimStart()
            if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
                inFence = !inFence
                return line
            }
            if (inFence) return line
            return line.replace(/([^\n])\s*(#{2,4})\s+(?=[0-9A-Za-z(])/g, '$1\n\n$2 ')
        })
        .join('\n')
}

/**
 * Theme-aware markdown renderer for agent responses.
 * Uses react-markdown + remark-gfm (tables, task lists, strikethrough).
 * Colors use Experience CSS variables so the renderer adapts to all 9 themes.
 * Code block backgrounds remain dark (intentional — standard across all themes).
 */
export const AgentMarkdownRenderer: React.FC<AgentMarkdownRendererProps> = ({
    content,
    isStreaming = false,
}) => {
    const processedContent = useMemo(() => {
        if (!content) return ''
        return normaliseContent(content)
    }, [content])

    return (
        <div style={{ wordBreak: 'break-word', lineHeight: 1.6, fontSize: 14 }}>
            <ReactMarkdown
                skipHtml
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ children }) => (
                        <div style={{ margin: '4px 0', lineHeight: '1.6' }}>{children}</div>
                    ),
                    strong: ({ children }) => (
                        <strong style={{ fontWeight: 700, color: 'var(--main-text, #1e293b)' }}>{children}</strong>
                    ),
                    em: ({ children }) => (
                        <em style={{ fontStyle: 'italic', color: 'var(--main-text-secondary, #475569)' }}>{children}</em>
                    ),
                    h1: ({ children }) => (
                        <h1
                            style={{
                                fontSize: 18,
                                fontWeight: 700,
                                margin: '16px 0 8px',
                                color: 'var(--main-text, #0f172a)',
                                borderBottom: '1px solid var(--divider-color, #e2e8f0)',
                                paddingBottom: 6,
                            }}
                        >
                            {children}
                        </h1>
                    ),
                    h2: ({ children }) => (
                        <h2
                            style={{
                                fontSize: 16,
                                fontWeight: 700,
                                margin: '14px 0 6px',
                                color: 'var(--main-text, #1e293b)',
                                borderBottom: '1px solid var(--divider-color, #e2e8f0)',
                                paddingBottom: 4,
                            }}
                        >
                            {children}
                        </h2>
                    ),
                    h3: ({ children }) => (
                        <h3
                            style={{ fontSize: 14, fontWeight: 700, margin: '12px 0 4px', color: 'var(--main-text, #334155)' }}
                        >
                            {children}
                        </h3>
                    ),
                    ul: ({ children }) => (
                        <ul style={{ margin: '6px 0', paddingLeft: 20 }}>{children}</ul>
                    ),
                    ol: ({ children }) => (
                        <ol style={{ margin: '6px 0', paddingLeft: 20 }}>{children}</ol>
                    ),
                    li: ({ children }) => (
                        <li style={{ margin: '3px 0', lineHeight: '1.5' }}>{children}</li>
                    ),
                    table: ({ children }) => (
                        <div style={{ overflowX: 'auto', margin: '10px 0', borderRadius: 6 }}>
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: 13,
                                    border: '1px solid var(--divider-color, #e2e8f0)',
                                }}
                            >
                                {children}
                            </table>
                        </div>
                    ),
                    th: ({ children }) => (
                        <th
                            scope="col"
                            style={{
                                padding: '8px 12px',
                                textAlign: 'left',
                                background: 'var(--table-header-bg, #f1f5f9)',
                                color: 'var(--main-text, #1e293b)',
                                fontWeight: 600,
                                borderBottom: '2px solid var(--divider-color, #cbd5e1)',
                                borderRight: '1px solid var(--divider-color, #e2e8f0)',
                            }}
                        >
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td
                            style={{
                                padding: '7px 12px',
                                borderBottom: '1px solid var(--divider-color, #e2e8f0)',
                                borderRight: '1px solid var(--divider-color, #e2e8f0)',
                                color: 'var(--main-text, #334155)',
                                verticalAlign: 'top',
                            }}
                        >
                            {children}
                        </td>
                    ),
                    code: ({ inline, className, children }: any) => {
                        const text = Array.isArray(children) ? children.join('') : String(children ?? '')
                        if (inline) {
                            return (
                                <code
                                    style={{
                                        background: 'var(--main-bg-elevated, #f1f5f9)',
                                        color: 'var(--main-text, #0f172a)',
                                        padding: '2px 5px',
                                        borderRadius: 4,
                                        fontSize: 13,
                                        fontFamily: "'Courier New', monospace",
                                    }}
                                >
                                    {text}
                                </code>
                            )
                        }
                        return (
                            <code
                                className={className}
                                style={{ fontFamily: "'Courier New', monospace", fontSize: 13 }}
                            >
                                {text.trimEnd()}
                            </code>
                        )
                    },
                    // Code block background stays dark — intentional across all themes
                    pre: ({ children }) => (
                        <pre
                            style={{
                                background: '#1e293b',
                                color: '#f1f5f9',
                                padding: '10px 14px',
                                borderRadius: 8,
                                overflowX: 'auto',
                                margin: '8px 0',
                                fontSize: 13,
                                fontFamily: "'Courier New', monospace",
                            }}
                        >
                            {children}
                        </pre>
                    ),
                    a: ({ href, children }) => {
                        const safe =
                            typeof href === 'string' &&
                            /^(https?:\/\/|mailto:|tel:|\/|\.\/|\.\.\/)/.test(href)
                        if (!safe) return <span style={{ color: 'var(--main-text-secondary, #64748b)' }}>{children}</span>
                        return (
                            <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'var(--link-color, #3b82f6)', textDecoration: 'underline' }}
                            >
                                {children}
                            </a>
                        )
                    },
                    hr: () => (
                        <hr style={{ border: 'none', borderTop: '1px solid var(--divider-color, #e2e8f0)', margin: '12px 0' }} />
                    ),
                    blockquote: ({ children }) => (
                        <blockquote
                            style={{
                                borderLeft: '3px solid var(--divider-color, #cbd5e1)',
                                margin: '8px 0',
                                paddingLeft: 12,
                                color: 'var(--main-text-secondary, #64748b)',
                                fontStyle: 'italic',
                            }}
                        >
                            {children}
                        </blockquote>
                    ),
                }}
            >
                {processedContent}
            </ReactMarkdown>
            {isStreaming && (
                <span
                    aria-hidden
                    style={{
                        display: 'inline-block',
                        width: 7,
                        height: 14,
                        background: 'var(--main-accent, #3b82f6)',
                        marginLeft: 2,
                        borderRadius: 1,
                        verticalAlign: 'text-bottom',
                        animation: 'eid-cursor-blink 1s infinite',
                    }}
                />
            )}
        </div>
    )
}

// Inject cursor blink animation once
if (
    typeof document !== 'undefined' &&
    !document.head.querySelector('style[data-eid-md-anim]')
) {
    const style = document.createElement('style')
    style.setAttribute('data-eid-md-anim', 'true')
    style.textContent = `@keyframes eid-cursor-blink { 0%,50%{opacity:1} 51%,100%{opacity:0} }`
    document.head.appendChild(style)
}

export default AgentMarkdownRenderer
